import React, { useEffect, useState } from 'react';

interface PageNotFoundProps {
  onRedirect?: () => void;
}

const PageNotFound: React.FC<PageNotFoundProps> = ({ onRedirect }) => {
  const [countdown, setCountdown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsRedirecting(true);
          // Simulate redirect after a brief delay for visual feedback
          setTimeout(() => {
            if (onRedirect) {
              onRedirect();
            } else {
              window.location.href = '/';
            }
          }, 500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onRedirect]);

  const handleGoHome = () => {
    setIsRedirecting(true);
    setTimeout(() => {
      if (onRedirect) {
        onRedirect();
      } else {
        window.location.href = '/';
      }
    }, 300);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="page-not-found-container">
      {/* Animated background */}
      <div className="bg-animation">
        <div className="bg-animation__before"></div>
        <div className="bg-animation__after"></div>
      </div>
      
      <div className="container">
        <div className="error-card">
          <i className="error-icon fas fa-exclamation-triangle"></i>
          <h1 className="glitch-text">404 Error</h1>
          <h2>Page Not Found</h2>
          
          <p>
            Looks like you've wandered into uncharted territory. This page doesn't exist. Let's get you back on track.
          </p>

          <div className="redirect-info">
            <div 
              className="timer-circle" 
              style={{ 
                transform: `rotate(${(5 - countdown) * 72}deg)`,
                borderColor: `rgba(0, 212, 255, 0.3) rgba(0, 212, 255, 0.3) rgba(0, 212, 255, 0.3) var(--primary-color)`
              }}
            ></div>
            <span className="timer-text">
              Redirecting in <span id="countdown">{countdown}</span> seconds...
            </span>
          </div>

          <div className="btn-container">
            <button 
              className="btn" 
              onClick={handleGoHome}
              disabled={isRedirecting}
            >
              <i className="fas fa-home"></i> Return Home
            </button>
            <button 
              className="btn secondary" 
              onClick={handleGoBack}
            >
              <i className="fas fa-arrow-left"></i> Go Back
            </button>
          </div>

          <p className="footer-text">
            <i className="fas fa-shield-alt"></i> This page was generated by BSides Bangalore Security Team
          </p>
        </div>
      </div>

      <style >{`
        :root {
          --primary-color: #00d4ff;
          --secondary-color: #dc143c;
          --text-color: #ffffff;
          --bg-color: #0d1d31;
          --card-bg: #1a2a3d;
        }

        .page-not-found-container {
          background: linear-gradient(135deg, #0d1d31 0%, #1a2a3d 100%);
          color: var(--text-color);
          font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
        }

        /* Animated background */
        .bg-animation {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          opacity: 0.3;
        }

        .bg-animation__before,
        .bg-animation__after {
          content: '';
          position: absolute;
          border-radius: 50%;
          background: var(--primary-color);
          animation: float 6s ease-in-out infinite;
        }

        .bg-animation__before {
          width: 200px;
          height: 200px;
          top: -100px;
          left: -100px;
          animation-delay: 0s;
        }

        .bg-animation__after {
          width: 300px;
          height: 300px;
          bottom: -150px;
          right: -150px;
          background: var(--secondary-color);
          animation-delay: 3s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .container {
          text-align: center;
          padding: 40px;
          max-width: 600px;
          width: 90%;
        }

        .error-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .error-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
        }

        .error-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
        }

        .error-icon {
          font-size: 80px;
          margin-bottom: 20px;
          display: block;
          color: var(--primary-color);
          text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        h1 {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 10px;
          background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        h2 {
          font-size: 24px;
          font-weight: 300;
          color: #ffffff;
          margin-bottom: 20px;
          text-transform: uppercase;
        }

        p {
          font-size: 16px;
          line-height: 1.6;
          color: #cfd8dc;
          margin-bottom: 30px;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        .redirect-info {
          background: rgba(0, 212, 255, 0.1);
          border: 1px solid rgba(0, 212, 255, 0.3);
          border-radius: 10px;
          padding: 15px;
          margin: 20px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
        }

        .timer-circle {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(0, 212, 255, 0.3);
          border-top: 3px solid var(--primary-color);
          border-radius: 50%;
          animation: spin 5s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .timer-text {
          font-size: 18px;
          font-weight: bold;
          color: var(--primary-color);
        }

        .btn-container {
          display: flex;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 20px;
        }

        .btn {
          display: inline-block;
          padding: 12px 30px;
          background: linear-gradient(135deg, var(--primary-color), #00a8cc);
          color: #000000;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
          border: none;
          font-size: 14px;
          font-family: inherit;
          min-width: 160px;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 212, 255, 0.5);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .btn.secondary {
          background: transparent;
          border: 2px solid var(--secondary-color);
          color: var(--secondary-color);
          box-shadow: none;
        }

        .btn.secondary:hover:not(:disabled) {
          background: var(--secondary-color);
          color: white;
          box-shadow: 0 4px 15px rgba(220, 20, 60, 0.3);
        }

        .footer-text {
          margin-top: 30px;
          font-size: 12px;
          color: #607d8b;
          opacity: 0.7;
        }

        /* Cyberpunk glitch effect */
        .glitch-text {
          position: relative;
          animation: glitch 1s steps(2, end) infinite;
        }

        @keyframes glitch {
          2%, 64% { transform: translate(0) skew(0); }
          4% { transform: translate(-2px, 0) skew(-5deg); }
          8% { transform: translate(2px, 0) skew(5deg); }
          10% { transform: translate(-4px, 0) skew(-10deg); }
          12% { transform: translate(4px, 0) skew(10deg); }
          14% { transform: translate(-8px, 0) skew(-15deg); }
          16% { transform: translate(8px, 0) skew(15deg); }
          18% { transform: translate(-8px, 0) skew(-15deg); }
          20% { transform: translate(8px, 0) skew(15deg); }
          22% { transform: translate(-4px, 0) skew(-10deg); }
          24% { transform: translate(4px, 0) skew(10deg); }
          26% { transform: translate(-2px, 0) skew(-5deg); }
          28% { transform: translate(2px, 0) skew(5deg); }
          30% { transform: translate(0) skew(0); }
          66% { transform: translate(-1px, 0) skew(-1deg); }
          68% { transform: translate(1px, 0) skew(1deg); }
          70% { transform: translate(0) skew(0); }
        }

        /* Responsive design */
        @media (max-width: 768px) {
          h1 {
            font-size: 36px;
          }
          
          h2 {
            font-size: 20px;
          }
          
          .error-icon {
            font-size: 60px;
          }
          
          .btn-container {
            flex-direction: column;
            align-items: center;
          }
          
          .btn {
            width: 100%;
            max-width: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default PageNotFound;